import { Component, HostListener, Input, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import * as FileSaver from 'file-saver';

import { FormBuilder, FormGroup } from '@angular/forms';
import { AppGlobals } from '@app/app.global';
import { EntityService, SharedService } from '@app/core';
import { DashboardService } from '@modules/dashboard/services';

@Component({
  selector: 'app-product',
  templateUrl: './product.component.html',
  styleUrls: ['./product.component.scss']
})
export class ProductComponent implements OnInit, OnDestroy {
  @Input() isTabActive: boolean;
  @Input() duration: { startDate: string, endDate: string, dateField: string };
  previousDuration: { startDate: string, endDate: string, dateField: string };
  productCount: { products: number, change: number };
  invoiceAmount: { amount: number, change: number };
  totalExpenseAmount: { totalExpenseAmount: number, change: number };
  isProductLoading: boolean = true;
  isSuppliersLoading: boolean = true;
  isInvoiceAmountLoading: boolean = true;
  isTotalExpenseLoading: boolean = true;
  isProductWiseSummaryLoading: boolean = true;
  isProductPriceTrendLoading: boolean = true;
  treemapSeries: {
    x: string,
    y: number,
    meta: {
      quantity: string,
      unit: string;
      percentage: number;
    }
  }[];
  mixedSeries: { name: string, type: string, data: any[] }[];
  mixedCategories: string[];
  mixedYAxisRange: { min: number, max: number }[];
  maxProductSelectionError: string = '';
  productNames: any[] = [];
  products: any[] = [];
  selectedProductNames: any[];
  supplierNames: any[] = [];
  selectedSupplierNames: any[] = [];
  trendProductNames: any[] = [];
  selectedTrendProductNames: any[];
  lineSeries: { name: string, data: any[] }[];
  private destroy$: Subject<void> = new Subject<void>();
  downloadReport: boolean = false;
  today: Date = new Date();
  itemWiseOverviewDuration: Date[] = [];
  itemWiseOverviewFilterForm: FormGroup;
  hideMobilePicker: boolean = true;
  dateShow: boolean = true;

  // Show date rangepicker for mobile isuue
  @HostListener('window:scroll', ['$event'])
  onScroll(event: Event) {
    if (this.dateShow) {
      event.stopPropagation();
      const ele = document.getElementsByClassName("date-mobile-product")[0];

      if (ele && !this.hideMobilePicker) {
        ele.classList.add("show");
        ele.classList.add("mobile-picker");
      }
    }
  }

  constructor(private dashboardService: DashboardService,
    public sharedService: SharedService,
    public appGlobals: AppGlobals,
    private formBuilder: FormBuilder,
    private entityService: EntityService) { }

  ngOnInit(): void {
    this.entityService.entitiesChanges$.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      if (this.isTabActive && this.duration?.startDate && this.duration?.endDate || res.isEntityChanged) {
        this.setPropsAndGetProductsData();
      }
    });

    this.itemWiseOverviewFilterForm = this.formBuilder.group({
      startDate: [null],
      endDate: [null],
      duration: ['']
    });
  }

  get g() { return this.itemWiseOverviewFilterForm.controls }

  ngOnChanges(changes: SimpleChanges) {
    if (this.isTabActive && this.duration?.startDate && this.duration?.endDate &&
      (this.duration?.startDate !== this.previousDuration?.startDate || this.duration?.endDate !== this.previousDuration?.endDate || this.duration?.dateField !== this.previousDuration?.dateField)) {
      this.setPropsAndGetProductsData();
    }
  }

  setPropsAndGetProductsData() {
    this.previousDuration = this.duration;
    this.isProductLoading = true;
    this.isTotalExpenseLoading = true;
    this.isInvoiceAmountLoading = true;
    this.isProductWiseSummaryLoading = true;
    this.getProductsCount();
  }

  getProductsCount() {
    this.dashboardService.getItemTabDashboard(this.duration).subscribe((res) => {
      this.isProductLoading = false;
      this.isTotalExpenseLoading = false;
      this.isInvoiceAmountLoading = false;
      this.isProductWiseSummaryLoading = false;
      this.isSuppliersLoading = false;

      this.productCount = res.productCount;
      this.totalExpenseAmount = res.totalExpenseCount;
      this.invoiceAmount = res.invoiceAmount;
      this.trendProductNames = res?.productWiseInvoiceSummary.map(item => item.description);
      this.getProductWiseInvoiceSummary(res.itemWiseTaxableAmount);

      this.selectedProductNames = res.priceWiseTopNProducts;
      if (res.priceWiseTopNProducts?.length) {
        this.selectedTrendProductNames = [res.priceWiseTopNProducts[0]];
        this.getProductPriceDetail();
        this.getProductPriceTrend();
      } else {
        this.selectedTrendProductNames = [];
        this.isProductPriceTrendLoading = false;
      }

      this.supplierNames = res.suppliers?.map(supplier => supplier.name).filter(supplier => supplier);
    }, (error: any) => {
      this.sharedService.errorAlert(error);
      this.isProductLoading = false;
      this.isTotalExpenseLoading = false;
      this.isInvoiceAmountLoading = false;
      this.isProductWiseSummaryLoading = false;
      this.isSuppliersLoading = false;
    });
  }

  // getTotalExpenseCount() {
  //   this.isTotalExpenseLoading = true;
  //   this.dashboardService.getTotalExpenseCount(this.duration).subscribe((res) => {
  //     this.isTotalExpenseLoading = false;
  //     this.totalExpenseAmount = res;
  //     this.getInvoiceAmount();
  //   }, (error: any) => {
  //     this.sharedService.errorAlert(error);
  //     this.isProductLoading = false;
  //   });
  // }

  // getInvoiceAmount() {
  //   this.isInvoiceAmountLoading = true;
  //   this.dashboardService.getInvoiceItemAmount(this.duration).subscribe(
  //     (res) => {
  //       this.invoiceAmount = res;
  //       this.isInvoiceAmountLoading = false;
  //       this.getSuppliers();
  //       this.getProductWiseInvoiceSummary();
  //     },
  //     (error: any) => {
  //       this.sharedService.errorAlert(error);
  //       this.isInvoiceAmountLoading = false;
  //     }
  //   );
  // }

  getProductWiseInvoiceSummary(res) {
    // this.isProductWiseSummaryLoading = true;
    // this.dashboardService.getProductWiseSummary(this.duration).subscribe(
    //   (res) => {
    this.isProductWiseSummaryLoading = false;
    const sum = res.reduce((sum, stat) => sum + stat.price, 0);
    const modifiedProducts = res.reduce((final, stat) => {
      if (!sum || stat.price / sum > 0.01) {
        final.products.push(stat);
      } else {
        final.otherProducts.push(stat);
      }

      return final;
    }, { products: [], otherProducts: [] });
    this.treemapSeries = [
      ...modifiedProducts.products.map(product => ({
        ...product,
        description: product.description
      })),
      ...(modifiedProducts.otherProducts?.length ? [modifiedProducts.otherProducts.reduce((final, stat) => ({
        description: 'Other',
        quantity: final.quantity + stat.quantity,
        price: final.price + stat.price,
        unit: final.unit ? final.unit : stat.unit
      }), { quantity: 0, price: 0, unit: 0 })] : [])
    ].map(stat => ({
      x: stat.description,
      y: stat.price,
      meta: {
        quantity: this.sharedService.formatNumber(stat.quantity, false, 0, 2, true),
        unit: stat.unit,
        percentage: 0
      },
    }));

    // Calculate the sum of values for the series
    const totalSum = this.treemapSeries.reduce((a: any, b: any) => a + b.y, 0);
    this.treemapSeries.forEach((seriesObject) => {
      const value = seriesObject.y;
      seriesObject.meta.percentage = +(totalSum ? (value / totalSum) * 100 : 0).toFixed(0);
    })
    this.mixedCategories = res.map(stat => stat.description);

    const productPrice = res.map((stat) => stat.price);
    this.mixedSeries = [
      {
        name: "Total Amount",
        type: "column",
        data: productPrice
      }
    ];
    this.mixedYAxisRange = [
      {
        min: Math.min(...productPrice),
        max: Math.max(...productPrice)
      }
    ];
    // this.isProductWiseSummaryLoading = false;
    //   },
    //   (error: any) => this.sharedService.errorAlert(error)
    // );
  }

  treemapCustomTooltip = ({ series, seriesIndex, dataPointIndex, w }) => {
    const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
    const color = w.config.colors[dataPointIndex];

    // Calculate the sum of values for the series
    const sum = series[seriesIndex].reduce((a, b) => a + b, 0);

    // Calculate the percentage for the current data point
    const value = series[seriesIndex][dataPointIndex];
    const percent = sum ? (value / sum) * 100 : 0;

    return ('<div class="arrow_box">' +
      '<div class="apexcharts-tooltip-title apxcharts-active">' +
      '<span class="color-item" style="background-color:' + color + '" ></span>' +
      this.sharedService.capitalize(data.x) +
      '</div>' +
      '<div class="custom-tooltip-data"> Qty.: <strong>' + this.sharedService.formatNumber(data.meta?.quantity, false, 0, 2, true) + ' ' + data.meta?.unit +
      ' </strong></div>' + '<div class="custom-tooltip-data"> Total Purchase: <strong>' + this.sharedService.formatNumber(series[seriesIndex][dataPointIndex], true, 0, 2, true) + ' (' + percent.toFixed(0) + '%)' + '</strong></div>' +
      '</div>');
  }

  // getTopProducts() {
  //   this.dashboardService.topProductList({ count: `${this.appGlobals.maxProductSearchLimit}`, ...this.duration }).subscribe(
  //     (res) => {
  //       this.selectedProductNames = res;

  //       if (res?.length) {
  //         this.selectedTrendProductNames = [res[0]];
  //       } else {
  //         this.selectedTrendProductNames = [];
  //       }
  //       this.getProductPriceDetail();
  //       this.getProductPriceTrend();
  //     },
  //     (error: any) => {
  //       this.sharedService.errorAlert(error);
  //     }
  //   );
  // }

  // getSuppliers() {
  //   this.isSuppliersLoading = true;
  //   this.dashboardService.getSuppliers(this.duration).subscribe((res) => {
  //     this.supplierNames = res.map(supplier => supplier.name).filter(supplier => supplier);
  //     if (this.supplierNames.length > 1) {
  //       this.supplierNames = this.supplierNames?.slice(0, -1);
  //     }
  //     this.isSuppliersLoading = false;
  //   }, (error: any) => {
  //     this.sharedService.errorAlert(error);
  //     this.isSuppliersLoading = false;
  //   }
  //   );
  // }

  onSearchProductPriceTrend(event: any) {
    // Split the searchQuery by spaces and count the number of words
    const searchTerm: string = event.term.trim();
    // Check if the number of words is greater than or equal to three
    if (searchTerm.length >= this.appGlobals.minAsyncSearchCharacter) {
      this.dashboardService.productList(searchTerm, this.duration).subscribe((res) => {
        this.trendProductNames = res;
      }, (error: any) => this.sharedService.errorAlert(error)
      );
    } else {
      this.trendProductNames = [];
    }
  }

  getProductPriceTrend() {
    if (this.selectedTrendProductNames.length > this.appGlobals.maxProductSearchLimit) {
      this.selectedTrendProductNames = this.selectedTrendProductNames.slice(0, -1);
      this.sharedService.errorAlert('You cannot search more than ' + this.appGlobals.maxProductSearchLimit + ' items');
      return;
    }

    const filters = { names: this.selectedTrendProductNames, ...this.duration };

    if (this.selectedSupplierNames.length) {
      filters['suppliers'] = this.selectedSupplierNames;
    }

    this.isProductPriceTrendLoading = true;
    this.dashboardService.productPriceTrend(filters).subscribe((res) => {
      this.lineSeries = res.filter(stat => stat?.trends.length)
        .map(stat => ({
          name: 'Rate for ' + this.sharedService.capitalize(stat.product) +
            (stat.supplier ? ' from ' + this.sharedService.capitalize(stat.supplier) : ''),
          data: stat.trends.filter(trend => trend.date).map(trend => ({
            x: +trend.date,
            y: trend.rate ?? 0,
            meta: {
              product: stat.product,
              supplier: stat.supplier,
              latestRate: stat.latestRate,
              quantity: trend.quantity,
              unit: trend.unit
            }
          }))
        }));
      this.isProductPriceTrendLoading = false;
    }, (error: any) => {
      this.sharedService.errorAlert(error);
      this.isProductPriceTrendLoading = false;
    });
  }

  onSearchProductWiseAnalysis(event: any) {
    // Split the searchQuery by spaces and count the number of words
    const searchItem: string = event.term.trim();
    // Check if the number of words is greater than or equal to three
    if (searchItem.length >= this.appGlobals.minAsyncSearchCharacter) {
      this.dashboardService.productList(searchItem, this.duration).subscribe(
        (res) => {
          this.productNames = res;
        },
        (error: any) => this.sharedService.errorAlert(error)
      );
    } else {
      this.productNames = [];
    }
  }

  getProductPriceDetail() {
    this.maxProductSelectionError = '';

    if (this.selectedProductNames.length > this.appGlobals.maxProductSearchLimit) {
      this.selectedProductNames = this.selectedProductNames.slice(0, -1);
      this.maxProductSelectionError = 'You cannot search more than ' + this.appGlobals.maxProductSearchLimit + ' items';
      this.sharedService.errorAlert('You cannot search more than ' + this.appGlobals.maxProductSearchLimit + ' items');
      return;
    }

    const duration = {
      startDate: this.g.duration.value ? new Date(this.g.startDate.value).toLocaleDateString('en-US') : this.duration.startDate,
      endDate: this.g.duration.value ? new Date(this.g.endDate.value).toLocaleDateString('en-US') : this.duration.endDate,
      dateField: this.duration.dateField,
    };

    this.dashboardService.productPriceDetails({ names: this.selectedProductNames, ...duration }).subscribe((res) => {
      this.products = res;
    }, (error: any) => this.sharedService.errorAlert(error));
  }

  downloadSupplierItemWiseReport() {
    this.downloadReport = true;
    this.dashboardService.getSupplierItemWiseSummaryReport(this.duration).subscribe(result => {
      this.exportSupplierItemReportToCsv(result);
      this.downloadReport = false;
    }, error => {
      console.error(error.message);
      this.sharedService.errorAlert('Error while downloading report');
      this.downloadReport = false;
    })
  }

  exportSupplierItemReportToCsv(dataToExport) {
    if (dataToExport.length) {
      const separator = ',';

      const allcolumns = [{
        supplier: 'Supplier name',
        gst: 'GST Number',
        pan: 'Pan Number',
        invoiceDate: 'Invoice Date',
        invoiceNo: 'Invoice Number',
        description: 'Description',
        priceVariation: 'Price Variation Over Time',
        category: 'Category',
        quantity: 'Quantity',
        hsn: 'HSN Code/SAC',
        rate: 'Rate',
        amount: 'Amount',
        discountPT: 'Discount (Item Level)',
        unit: 'Unit',
        cgst: 'CGST',
        cgstRate: 'CGST Rate',
        cgst2point5percent: 'CGST 2.5%',
        cgst6percent: 'CGST 6%',
        cgst9percent: 'CGST 9%',
        cgst14percent: 'CGST 14%',
        sgst: 'SGST',
        sgstRate: 'SGST Rate',
        sgst2point5percent: 'SGST 2.5%',
        sgst6percent: 'SGST 6%',
        sgst9percent: 'SGST 9%',
        sgst14percent: 'SGST 14%',
        igst: 'IGST',
        igstRate: 'IGST Rate',
        igst5percent: 'IGST 5%',
        igst12percent: 'IGST 12%',
        igst18percent: 'IGST 18%',
        igst28percent: 'IGST 28%',
        cess: 'CESS',
        vat: 'VAT',
        excise: 'Excise',
        tds: 'TDS',
        tcs: 'TCS',
        otherCharges: 'Other Charges',
        // otherChargesTax: 'Other Charges Tax',
        freightCharges: 'Freight Charges',
        // freightChargesTax: 'Freight Charges Tax',
        deliveryCharges: 'Delivery Charges',
        // deliveryChargesTax: 'Delivery Charges Tax',
        shippingCharges: 'Shipping Charges',
        // shippingChargesTax: 'Shipping Charges Tax',
        transportationCharges: 'Transportation Charges',
        // transportationChargesTax: 'Transportation Charges Tax',
        insuranceCharges: 'Insurance Charges',
        // insuranceChargesTax: 'Insurance Charges Tax',
        packingAndForwardingCharges: 'Packing And Forwarding Charges',
        // packingAndForwardingChargesTax: 'Packing And Forwarding Charges Tax',
        fuelCharges: 'Fuel Charges',
        // fuelChargesTax: 'Fuel Charges Tax',
        packingCharges: 'Packing Charges',
        // packingChargesTax: 'Packing Charges Tax',
        labourCharges: 'Labour Charges',
        // labourChargesTax: 'Labour Charges Tax',
        courierCharges: 'courier Charges',
        // courierChargesTax: 'courierChargesTax',
        freightAndPackingCharges: 'Freight And Packing Charges',
        // freightAndPackingChargesTax: 'Freight And Packing Charges Tax',
        discountTT: 'Discount (Invoice Level)',
        total: 'Total',
        purchaseExpense: 'Purchase/Expense',
      }];

      const keys = Object.keys(dataToExport[0]);

      const columns = [];
      keys.forEach(key => {
        const findColumnName = allcolumns.find(columns => columns[key]);
        columns.push(findColumnName[key]);
      })

      const rowsContent = dataToExport.map(row => {
        return keys.map(k => {
          let cell = row[k] ?? '';
          cell = cell instanceof Date
            ? cell.toLocaleString()
            : cell.toString().replace(/"/g, '""');
          if (cell.search(/("|,|\n)/g) >= 0) {
            cell = `"${cell}"`;
          }
          return cell;
        }).join(separator)
      }).join('\n');

      const csvContent = columns.join(separator) + '\n' + rowsContent;
      const data = new Blob([csvContent], { type: 'text/plain;charset=utf-8' });

      FileSaver.saveAs(data, `Item-wise Report ${new Date(this.duration?.startDate).toLocaleDateString('en-CA')} to ${new Date(this.duration?.endDate).toLocaleDateString('en-CA')}.csv`);
    };
  }

  onDurationPickerOpen() {
    if (this.g.startDate.value && this.g.endDate.value) {
      this.itemWiseOverviewDuration = [
        this.g.startDate.value,
        this.g.endDate.value
      ];
    } else {
      this.itemWiseOverviewDuration = [];
    }
    // set flag for mobile isuue
    this.hideMobilePicker = false;
  }


  getDuration(predefinedDuration: string) {
    let duration: Date[] = [];

    switch (predefinedDuration) {
      case 'today':
        duration = [
          new Date(),
          new Date()
        ];
        break;
      case 'thisWeek':
        duration = [
          new Date(new Date().setDate(new Date().getDate() - new Date().getDay())),
          new Date()
        ];
        break;
      case 'lastWeek':
        const firstDayOfTheWeek = new Date(new Date().setDate(new Date().getDate() - new Date().getDay()));
        const lastDayOfTheWeek = new Date(new Date().setDate(new Date().getDate() - new Date().getDay() + 6));
        duration = [
          new Date(firstDayOfTheWeek.setDate(firstDayOfTheWeek.getDate() - 7)),
          new Date(lastDayOfTheWeek.setDate(lastDayOfTheWeek.getDate() - 7))
        ];
        break;
      case 'fortnight':
        duration = [
          new Date(new Date().setDate(new Date().getDate() - 15)),
          new Date()
        ];
        break;
      case 'MTD':
        duration = [
          new Date(new Date().setDate(1)),
          new Date()
        ];
        break;
      case 'YTD':
        duration = [
          new Date(new Date().getFullYear(), 0, 1),
          new Date()
        ];
        break;
      case 'custom':
        duration = this.itemWiseOverviewDuration;
        break;
      case 'clear':
        duration = [];
        break;
      case 'reset':
        duration = this.itemWiseOverviewDuration;
        break;
    }

    this.itemWiseOverviewDuration = duration;
  }

  setDuration() {
    if (this.itemWiseOverviewDuration?.length) {
      this.g.startDate.setValue(this.itemWiseOverviewDuration[0]);
      this.g.endDate.setValue(this.itemWiseOverviewDuration[1]);
      this.g.duration.setValue(
        this.itemWiseOverviewDuration[0].toLocaleDateString('en-US', this.appGlobals.dateFormat) + ' to ' +
        this.itemWiseOverviewDuration[1].toLocaleDateString('en-US', this.appGlobals.dateFormat)
      );
      this.getOverviewDatePicker();
    } else {
      this.g.startDate.setValue(null);
      this.g.endDate.setValue(null);
      this.g.duration.setValue('');
      this.getProductsCount();
    }
  }


  durationChange(duration: Date[]) {
    this.itemWiseOverviewDuration = duration;
  }

  getOverviewDatePicker() {
    const duration = {
      startDate: new Date(this.g.startDate.value).toLocaleDateString('en-US'),
      endDate: new Date(this.g.endDate.value).toLocaleDateString('en-US'),
      dateField: this.duration.dateField,
    };

    this.isProductWiseSummaryLoading = true;

    this.dashboardService.getItemTabDashboard(duration).subscribe((res) => {
      this.isProductWiseSummaryLoading = false;

      if (res.priceWiseTopNProducts?.length) {
        this.productNames = res.priceWiseTopNProducts;
        this.selectedProductNames = res.priceWiseTopNProducts;
        this.getProductPriceDetail();
      } else {
        this.productNames = [];
        this.selectedProductNames = [];
        this.products = [];
      }

    }, (error: any) => {
      this.sharedService.errorAlert(error);
      this.isProductWiseSummaryLoading = false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$?.next();
    this.destroy$?.complete();
  }
}